import React from "react";
import { useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import ReactFlow, { Edge, Node, useEdgesState, useNodesState, Background, ReactFlowInstance, addEdge, MiniMap, BackgroundVariant, ConnectionLineType } from "reactflow";
import axios from "axios";
import { workflowItems } from "../../pages/workflow";
import { CustomNode } from "../nodes";
import { CustomEdge } from "../edges";
import { RunActions } from "../run";
import { RootState } from "../../redux/store";
import { consoleURL, webURL } from "../../services/API";
import { handleWorkflowSave } from "../../services/method";
import { Main } from "./style";
// @ts-ignore
import 'reactflow/dist/style.css';
import { ProjectLog } from "../../pages/projects/log";
import { MonitorLog } from "./monitorLog";
import { useDispatch } from 'react-redux';
import { setProjectData } from '../../redux/reducers/project'
import { LiveLog } from "./liveLog";
import { IssuesLog } from "./issuesLog";
import { CanvasControls } from "./canvasControls";
import { AlignmentGuides } from "./alignmentGuides";
import socket from "../../services/socket";
import { AiAssistant } from "../aiAssistant";
import { AI_ASSISTANT_DEFAULTS } from "../../config/aiAssistantConfig";
import { enrichNodeFromCatalog } from "../../services/aiAssistant/applyPatch";
import { applySpreadsheetToNodeList } from "../run/globalConnections/spreadsheetUtils";
import { BrowserUsePreview } from "../run/browserPreview";
import { ComputerUsePreview } from "../run/computerPreview";

const nodeTypes = { customNode: CustomNode };
const edgeTypes = { customEdge: CustomEdge };
const MIN_CANVAS_ZOOM = 0.1;
const MEDIUM_OPEN_ZOOM = 0.5;
let timeoutId: any = 0;

type Snapshot = { nodes: Node[]; edges: Edge[] };
type PendingTemplate = { id: string; title: string };

export function View() {

   const userToken = useSelector((state: RootState) => state.user.data?.token);
   const isWorkflowRun = useSelector((state: RootState) => state.settings.data.isWorkflowRun);
   const showContactUS = useSelector((state: RootState) => state.settings.data.showContactUS);
   const isThereBlockExpanded = useSelector((state: RootState) => state.project.isThereBlockExpanded);
   const [searchParams] = useSearchParams();
   const rawProjectId = searchParams.get('id');
   const projectId =
      rawProjectId && rawProjectId !== 'undefined' && rawProjectId !== 'null'
         ? rawProjectId
         : null;
   const [loadError, setLoadError] = React.useState<string | null>(null);
   const [toggleMap, setToggleMap] = React.useState<boolean>(false);
   const [nodes, setNodes, onNodesChange] = useNodesState<Node[]>([]);
   const [edges, setEdges, onEdgesChange] = useEdgesState<Edge[]>([]);
   const [project, setProject] = React.useState<projectType | null>(null);
   const [reactFlowInstance, setReactFlowInstance] = React.useState<ReactFlowInstance | null>(null);
   const initiallyFittedProjectId = React.useRef<string | null>(null);
   const [pendingTemplate, setPendingTemplate] = React.useState<PendingTemplate | null>(null);
   const [isApplyingTemplate, setIsApplyingTemplate] = React.useState<boolean>(false);
   const dispatch = useDispatch();
   // AI Assistant panel
   const [isAiOpen, setIsAiOpen] = React.useState<boolean>(false);
   const [aiWidth, setAiWidth] = React.useState<number>(AI_ASSISTANT_DEFAULTS.panelWidth);
   const [aiAutosaveLocked, setAiAutosaveLocked] = React.useState(false);
   const [aiAwaitingPersist, setAiAwaitingPersist] = React.useState(false);
   const aiPersistBaseline = React.useRef<Snapshot | null>(null);
   // ---- Undo / Redo  (tracks both nodes AND edges)
   const [history, setHistory] = React.useState<Snapshot[]>([]);
   const [future,  setFuture]  = React.useState<Snapshot[]>([]);
   // Prevents the snapshot effect from firing during undo/redo state restoration
   const isUndoRedoing = React.useRef(false);

   // Push a snapshot of the given nodes/edges onto the undo stack.
   const pushSnapshot = (nextNodes: Node[], nextEdges: Edge[]) => {
      if (isUndoRedoing.current) return;
      setHistory(h => [...h, { nodes: nextNodes, edges: nextEdges }].slice(-20));
      setFuture([]);
   };

   // Record a snapshot whenever the number of nodes or edges changes (add/remove a block or connection).
   React.useEffect(() => {
      pushSnapshot(nodes, edges);
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [nodes.length, edges.length]);

   // Record a snapshot once a node-move gesture finishes — not on every intermediate
   // position update while dragging, so one Undo reverts exactly one move, however
   // many sequential moves happened before it.
   const handleNodeDragStop = () => {
      setAlignmentGuides({ x: null, y: null });
      pushSnapshot(nodes, edges);
   };

   // ---- Automatic drag alignment ("smart guides", Figma-style, magnet-like)
   // While dragging any node, it snaps into exact alignment (left/center/right edges on X,
   // top/center/bottom edges on Y — independent of each other, so it can snap on one axis,
   // both, or neither) with whichever other node it comes within SNAP_THRESHOLD px of, and
   // shows a dashed guide line at the matched coordinate. Always on, no modifier key needed.
   const SNAP_THRESHOLD = 10;
   const [alignmentGuides, setAlignmentGuides] = React.useState<{ x: number | null; y: number | null }>({ x: null, y: null });

   const handleNodeDrag = React.useCallback((_event: React.MouseEvent, draggedNode: Node) => {
      const width = draggedNode.width || 0;
      const height = draggedNode.height || 0;
      const centerX = draggedNode.position.x + width / 2;
      const centerY = draggedNode.position.y + height / 2;

      let bestX: { diff: number; guide: number; snapTo: number } | null = null;
      let bestY: { diff: number; guide: number; snapTo: number } | null = null;

      for (const other of nodes) {
         if (other.id === draggedNode.id) continue;
         const ow = other.width || 0, oh = other.height || 0;

         const xCandidates = [
            { a: draggedNode.position.x, b: other.position.x },                 // left edges
            { a: centerX, b: other.position.x + ow / 2 },                        // centers
            { a: draggedNode.position.x + width, b: other.position.x + ow },     // right edges
         ];
         for (const c of xCandidates) {
            const diff = Math.abs(c.a - c.b);
            if (diff <= SNAP_THRESHOLD && (!bestX || diff < bestX.diff)) {
               bestX = { diff, guide: c.b, snapTo: draggedNode.position.x + (c.b - c.a) };
            }
         }

         const yCandidates = [
            { a: draggedNode.position.y, b: other.position.y },
            { a: centerY, b: other.position.y + oh / 2 },
            { a: draggedNode.position.y + height, b: other.position.y + oh },
         ];
         for (const c of yCandidates) {
            const diff = Math.abs(c.a - c.b);
            if (diff <= SNAP_THRESHOLD && (!bestY || diff < bestY.diff)) {
               bestY = { diff, guide: c.b, snapTo: draggedNode.position.y + (c.b - c.a) };
            }
         }
      }

      if (bestX || bestY) {
         setNodes(nds => nds.map(n => n.id !== draggedNode.id ? n : {
            ...n,
            position: {
               x: bestX ? bestX.snapTo : n.position.x,
               y: bestY ? bestY.snapTo : n.position.y,
            }
         }));
      }
      setAlignmentGuides({ x: bestX ? bestX.guide : null, y: bestY ? bestY.guide : null });
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [nodes, setNodes]);

   const handleUndo = React.useCallback(() => {
      if (isThereBlockExpanded) return;
      setHistory(prev => {
         if (prev.length <= 1) return prev;
         const next    = [...prev];
         const current = next.pop()!;
         const snap    = next[next.length - 1];
         setFuture(f => [...f, current]);
         isUndoRedoing.current = true;
         setNodes(snap.nodes);
         setEdges(snap.edges);
         // Clear the guard after React finishes re-rendering
         requestAnimationFrame(() => requestAnimationFrame(() => {
            isUndoRedoing.current = false;
         }));
         return next;
      });
   }, [isThereBlockExpanded, setNodes, setEdges]);

   const handleRedo = React.useCallback(() => {
      if (isThereBlockExpanded) return;
      setFuture(prev => {
         if (!prev.length) return prev;
         const next = [...prev];
         const snap = next.pop()!;
         isUndoRedoing.current = true;
         setNodes(snap.nodes);
         setEdges(snap.edges);
         setHistory(h => [...h, snap].slice(-20));
         requestAnimationFrame(() => requestAnimationFrame(() => {
            isUndoRedoing.current = false;
         }));
         return next;
      });
   }, [isThereBlockExpanded, setNodes, setEdges]);

   const canUndo = history.length > 1;
   const canRedo = future.length > 0;

   React.useEffect(() => {
      const handleKeys = (e: KeyboardEvent) => {
         if (isThereBlockExpanded) return;
         if (e.ctrlKey && e.code === 'KeyZ') handleUndo();
         if (e.ctrlKey && e.code === 'KeyY') handleRedo();
      };
      window.addEventListener('keydown', handleKeys);
      return () => window.removeEventListener('keydown', handleKeys);
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [isThereBlockExpanded, handleUndo, handleRedo]);

   // --- Function to fetch project data
   const fetchProjectData = React.useCallback(async () => {
      if (userToken && projectId) {
         try {
            const response = await axios.get(`${webURL}/projects/workflow?id=${projectId}`, { headers: { 'x-auth-token': userToken } });
            for (const node of response.data.nodes) {
               const action = workflowItems.find(item => item.action === node.data.action);
               if (action) {
                  node.data.executeRun = action.executeRun;
                  node.data.background = action.background;
                  node.data.icon = action.icon;
                  if (action.variablesRef) node.data.variablesRef = action.variablesRef;
                  else delete node.data.variablesRef;
                  node.data.postions = action.postions;
               }
            }
            const loadedNodes = response.data.globalGoogleSpreadsheet?.spreadsheet_id
               ? applySpreadsheetToNodeList(response.data.nodes, response.data.globalGoogleSpreadsheet)
               : response.data.nodes;
            setNodes(loadedNodes);
            setEdges(response.data.edges.map((e: any) => ({ ...e, animated: true })));
            setProject({ _id: projectId, ...response.data });
            dispatch(setProjectData({
               id: projectId, name: response.data.name, isLoop: !!response.data.loop?.isStarting,
               getExportAPIURL: `${webURL}/run/${projectId}`, getExportAPIKey: response.data.apiKey,
               getLastRunDate: response.data.lastRun, getDateOfCreation: response.data.registration,
               globalConnections: response.data.globalConnections || {},
               globalGoogleSpreadsheet: response.data.globalGoogleSpreadsheet || undefined,
            }));
            setLoadError(null);
            if (window.parent && window.parent !== window) {
               window.parent.postMessage({ type: 'WORKFLOW_READY', projectId }, '*');
            }
         } catch (err) {
            console.log(err);
            setLoadError('Could not load this workflow. It may be missing or you may not have access.');
            if (window.parent && window.parent !== window) {
               window.parent.postMessage({ type: 'WORKFLOW_READY', projectId, error: true }, '*');
            }
         }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [userToken, projectId]);

   // ---   
   React.useEffect(() => {
      if (userToken) {
         if (!projectId) {
            setLoadError('Missing workflow id. Open this project again from the console.');
            if (window.parent && window.parent !== window) {
               window.parent.postMessage({ type: 'WORKFLOW_READY', error: true }, '*');
            }
         } else {
            fetchProjectData();
         }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [userToken]);

   // Open every workflow centered at a medium zoom. Waiting until nodes are loaded
   // avoids the graph being stuck at the bottom-left corner.
   React.useEffect(() => {
      if (
         !reactFlowInstance ||
         !projectId ||
         !nodes.length ||
         initiallyFittedProjectId.current === projectId
      ) return;

      initiallyFittedProjectId.current = projectId;
      requestAnimationFrame(() => {
         requestAnimationFrame(() => {
            let minX = Infinity;
            let minY = Infinity;
            let maxX = -Infinity;
            let maxY = -Infinity;

            for (const node of nodes) {
               const width = node.width || 220;
               const height = node.height || 140;
               minX = Math.min(minX, node.position.x);
               minY = Math.min(minY, node.position.y);
               maxX = Math.max(maxX, node.position.x + width);
               maxY = Math.max(maxY, node.position.y + height);
            }

            reactFlowInstance.setCenter(
               (minX + maxX) / 2,
               (minY + maxY) / 2,
               { zoom: MEDIUM_OPEN_ZOOM, duration: 0 }
            );
         });
      });
   }, [reactFlowInstance, projectId, nodes, nodes.length]);

   // 
   React.useEffect(() => {
      const handleReFetchData = () => { if (!isWorkflowRun) fetchProjectData() };
      socket.off('reFetchData_workflowBuilder');
      socket.on('reFetchData_workflowBuilder', handleReFetchData);
      return () => { socket.off('reFetchData_workflowBuilder', handleReFetchData) }
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [fetchProjectData, isWorkflowRun]);

   // --- Listen for workflow refresh events from chat sidebar
   React.useEffect(() => {
      const handleRefreshWorkflow = () => { fetchProjectData() };
      window.addEventListener('refreshWorkflow', handleRefreshWorkflow);
      return () => window.removeEventListener('refreshWorkflow', handleRefreshWorkflow);
   }, [fetchProjectData]);

   // --- Autosave (paused while AI pending patch or awaiting explicit persist)
   React.useEffect(() => {
      clearTimeout(timeoutId);
      if (nodes.length && !aiAutosaveLocked && !aiAwaitingPersist) {
         timeoutId = setTimeout(() => {
            if (userToken && !isWorkflowRun) {
               handleWorkflowSave(searchParams.get('id') as string, userToken, nodes, edges);
            }
         }, 5000);
      }
      return () => clearTimeout(timeoutId);
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [userToken, nodes, edges, aiAutosaveLocked, aiAwaitingPersist]);

   const applyAiGraph = React.useCallback((next: Snapshot) => {
      const enrichedNodes = next.nodes.map((n) => enrichNodeFromCatalog(n));
      const enrichedEdges = next.edges.map((e) => ({ ...e, animated: true, type: e.type || 'customEdge' }));
      // Keep baseline for discard before first unsaved AI apply in this cycle
      if (!aiPersistBaseline.current) {
         aiPersistBaseline.current = {
            nodes: nodes.map((n) => ({ ...n, data: { ...n.data } })),
            edges: edges.map((e) => ({ ...e })),
         };
      }
      // Snapshot current graph before AI mutation (must happen before undo guard)
      setHistory((h) => [...h, { nodes, edges }].slice(-20));
      setFuture([]);
      isUndoRedoing.current = true;
      setNodes(enrichedNodes);
      setEdges(enrichedEdges);
      requestAnimationFrame(() => requestAnimationFrame(() => {
         isUndoRedoing.current = false;
      }));
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [nodes, edges, setNodes, setEdges]);

   const persistAiChanges = React.useCallback(async () => {
      if (!userToken || !projectId) return;
      await handleWorkflowSave(projectId, userToken, nodes, edges);
      aiPersistBaseline.current = null;
   }, [userToken, projectId, nodes, edges]);

   const discardAiChanges = React.useCallback(() => {
      const baseline = aiPersistBaseline.current;
      if (baseline) {
         isUndoRedoing.current = true;
         setNodes(baseline.nodes);
         setEdges(baseline.edges);
         requestAnimationFrame(() => requestAnimationFrame(() => {
            isUndoRedoing.current = false;
         }));
      } else {
         handleUndo();
      }
      aiPersistBaseline.current = null;
      setAiAwaitingPersist(false);
      setAiAutosaveLocked(false);
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [handleUndo, setNodes, setEdges]);

   // ---
   const onDragOver = React.useCallback((event: React.DragEvent<HTMLLIElement>) => {
      event.preventDefault(); event.dataTransfer.dropEffect = "move";
   }, []);

   const applyTemplate = React.useCallback(async (template: PendingTemplate) => {
      setIsApplyingTemplate(true);
      try {
         const data = (await axios.get(`${webURL}/templates?id=${template.id}`)).data;
         if (data.nodes) {
            for (const node of data.nodes) {
               const action = workflowItems.find(item => item.action === node.data.action);
               if (action) {
                  node.data.executeRun = action.executeRun;
                  node.data.background = action.background;
                  node.data.icon = action.icon;
                  node.data.postions = action.postions;
               }
            }
            setNodes(data.nodes);
            setEdges(data.edges);
            setPendingTemplate(null);
            requestAnimationFrame(() => {
               requestAnimationFrame(() => {
                  if (!reactFlowInstance || !data.nodes?.length) return;
                  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
                  for (const node of data.nodes) {
                     const width = node.width || 220;
                     const height = node.height || 140;
                     minX = Math.min(minX, node.position.x);
                     minY = Math.min(minY, node.position.y);
                     maxX = Math.max(maxX, node.position.x + width);
                     maxY = Math.max(maxY, node.position.y + height);
                  }
                  reactFlowInstance.setCenter(
                     (minX + maxX) / 2,
                     (minY + maxY) / 2,
                     { zoom: MEDIUM_OPEN_ZOOM, duration: 20 }
                  );
               });
            });
         }
      } catch (err) {
         console.log(err);
         alert('Unable to apply this template. Please try again.');
      } finally {
         setIsApplyingTemplate(false);
      }
   }, [reactFlowInstance, setEdges, setNodes]);

   const cancelTemplateApply = React.useCallback(() => {
      if (!isApplyingTemplate) setPendingTemplate(null);
   }, [isApplyingTemplate]);

   // ---
   const onDrop = React.useCallback((event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      const action = event.dataTransfer.getData("application/reactflow");

      if (action === 'template') {
         const id = event.dataTransfer.getData("text/plain");
         const title = event.dataTransfer.getData("application/reactflow/template-title") || 'this template';
         if (id) setPendingTemplate({ id, title });
      } else {
         const currentCategory = event.dataTransfer.getData("text/plain");
         if (!reactFlowInstance || typeof action === "undefined" || !action) return;
         const position = reactFlowInstance.screenToFlowPosition({ x: event.clientX, y: event.clientY });

         setNodes((nds) => {
            // Prevent multiple start nodes
            if (action === 'start' && nds.some((node: any) => node.data.action === 'start')) {
               alert('Only one Start node is allowed per workflow');
               return nds; // Return unchanged nodes
            }
            const filter = nds
               .filter((i: any) => i?.data?.action === action)
               .map((o: any) => {
                  const rawIndex = typeof o?.data?.index === 'string' ? o.data.index : '';
                  const matched = rawIndex.match(/\d+/);
                  return matched ? +matched[0] : null;
               })
               .filter((value: number | null): value is number => value !== null && Number.isFinite(value));
            const index = filter.length ? Math.max(...filter) + 1 : 1;
            const updated = nds.concat({
               id: (nds[nds.length - 1]?.id ? parseInt(nds[nds.length - 1].id) + 1 : 1).toString(), type: 'customNode', position,
               data: { index: action + '-' + (index), ...workflowItems.find(item => item.action === action), currentCategory }
            } as any);
            return updated;
         });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [reactFlowInstance]);

   // --
   const onConnect = React.useCallback((params: any) => { // @ts-ignore
      const findTarget = nodes.find(node => node.id === params.target); // @ts-ignore
      const isHide = findTarget?.data.postions.find(postion => postion.label === params.targetHandle)?.isHide
      if (isHide) return;

      params.type = 'customEdge'; // @ts-ignore
      params.animated = true;
      // @ts-ignore
      params.data = { color: findTarget?.data?.background };
      setEdges((eds) => {
         // Prevent multiple edges from start node
         const sourceNode = nodes.find(node => node.id === params.source) as any;
         if (sourceNode?.data.action === 'start') {
            const existingOutgoingEdge = eds.find(edge => edge.source === params.source);
            if (existingOutgoingEdge) {
               alert('Start node can only have one outgoing connection');
               return eds; // Return unchanged edges
            }
         }
         return addEdge(params, eds);
      });
      // Removed triggerAutoLayout() to prevent viewport changes when connecting nodes
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [nodes]);

   // ---
   const handleEdgeHover = (now: Edge, value: boolean) => {
      setEdges((edges) =>
         edges.map((edge) => {
            if (edge.id === now.id) return { ...edge, data: { ...now.data, isHover: value } };
            else return edge;
         })
      );
   }

   if (loadError) {
      return (
         <Main style={{ display: 'grid', placeItems: 'center', background: '#0a0f1a', color: '#e2e8f0' }}>
            <div style={{ textAlign: 'center', maxWidth: 420, padding: 24 }}>
               <h2 style={{ margin: '0 0 8px', fontSize: 18 }}>Workflow unavailable</h2>
               <p style={{ margin: '0 0 18px', color: '#94a3b8', lineHeight: 1.5 }}>{loadError}</p>
               <button
                  type="button"
                  onClick={() => {
                     const target = `${consoleURL}/workflow-builder`;
                     if (window.top && window.top !== window.self) window.top.location.href = target;
                     else window.location.href = target;
                  }}
                  style={{
                     border: '1px solid rgba(45, 212, 191, 0.45)',
                     background: 'rgba(45, 212, 191, 0.12)',
                     color: '#5eead4',
                     borderRadius: 999,
                     padding: '8px 16px',
                     fontWeight: 600,
                     cursor: 'pointer',
                  }}
               >
                  Back to Console
               </button>
            </div>
         </Main>
      );
   }

   return (
      <Main style={{ paddingRight: isAiOpen ? `${aiWidth}px` : '0px' }}>
         <ReactFlow
            proOptions={{ hideAttribution: true }}
            nodes={nodes}
            nodeTypes={nodeTypes}
            edges={edges}
            edgeTypes={edgeTypes}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onNodeDrag={handleNodeDrag}
            onNodeDragStop={handleNodeDragStop}
            onConnect={onConnect}
            onInit={setReactFlowInstance}
            onDrop={onDrop}
            onDragOver={onDragOver as any}
            onEdgeMouseEnter={(_, now) => handleEdgeHover(now, true)}
            onEdgeMouseLeave={(_, now) => handleEdgeHover(now, false)}
            minZoom={MIN_CANVAS_ZOOM}
            snapToGrid={true}
            snapGrid={[20, 20]}
            connectionLineType={ConnectionLineType.SmoothStep}
            style={{ backgroundColor: '#FBFAFE' }}
         >
            <Background
               variant={BackgroundVariant.Dots}
               gap={26}
               size={1.6}
               color="rgba(107, 101, 132, 0.28)"
            />
            <RunActions
               project={project}
               setProject={setProject}
               onUndo={handleUndo}
               onRedo={handleRedo}
               canUndo={canUndo}
               canRedo={canRedo}
            />
            <div className="bottomDock">
               <CanvasControls toggleMap={ toggleMap } setToggleMap={ setToggleMap } />
               <div className="logs">
                  <IssuesLog />
                  <LiveLog />
                  <MonitorLog projectId={userToken ? projectId as string : ''} />
                  <ProjectLog
                     projectId={userToken ? projectId as string : ''} // @ts-ignore
                     nodes={nodes}
                  />
               </div>
            </div>
            <AlignmentGuides x={alignmentGuides.x} y={alignmentGuides.y} />
            {toggleMap ? <MiniMap nodeStrokeWidth={3} /> : null}
            {/* AI Assistant (password-gated side panel) */}
            {projectId ? (
               <AiAssistant
                  projectId={projectId}
                  nodes={nodes}
                  edges={edges}
                  isOpen={isAiOpen}
                  onOpenChange={(open) => {
                     setIsAiOpen(open);
                     // Closing panel discards pending AI changes (panel manages its own pending state)
                  }}
                  width={aiWidth}
                  onWidthChange={setAiWidth}
                  onAutosaveLockChange={setAiAutosaveLocked}
                  onAwaitingPersistChange={setAiAwaitingPersist}
                  awaitingPersist={aiAwaitingPersist}
                  onApplyGraph={(next) => applyAiGraph(next)}
                  onPersistToServer={persistAiChanges}
                  onDiscardAiChanges={discardAiChanges}
                  onUndoAi={handleUndo}
                  onRedoAi={handleRedo}
                  canUndoAi={canUndo}
                  canRedoAi={canRedo}
               />
            ) : null}
            <div className={`contactUs ${ showContactUS ? 'show' : ''}`} onClick={ _=> window.open('https://calendly.com/cloudilic', '_blank') }>
               Having an issue? <span>Contact us</span>
            </div>
            {pendingTemplate ? (
               <div className="templateConfirmOverlay" onClick={cancelTemplateApply}>
                  <div className="templateConfirmModal" onClick={e => e.stopPropagation()}>
                     <h3>Apply Template?</h3>
                     <p>
                        This will replace the current workflow with <strong>{pendingTemplate.title}</strong>.
                     </p>
                     <div className="templateConfirmActions">
                        <button type="button" onClick={cancelTemplateApply} disabled={isApplyingTemplate}>
                           Cancel
                        </button>
                        <button type="button" className="primary" onClick={() => applyTemplate(pendingTemplate)} disabled={isApplyingTemplate}>
                           {isApplyingTemplate ? 'Applying...' : 'Apply Template'}
                        </button>
                     </div>
                  </div>
               </div>
            ) : null}
         </ReactFlow>
         <BrowserUsePreview
            projectId={projectId}
            isRunning={isWorkflowRun}
            enabled={nodes.some((node: any) => node.data?.action === "browserUse")}
         />
         <ComputerUsePreview
            projectId={projectId}
            apiKey={project?.apiKey}
            isRunning={isWorkflowRun}
            enabled={nodes.some((node: any) => node.data?.action === "computerUse")}
         />
      </Main>
   );
}  
